import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { subscriptionsApi } from '../api/subscriptionsApi';
import { getSiteOrigin, joinUrl } from '../utils/seo';

const TABS = [
  { id: 'calendar', label: 'Календарь', icon: '🗓' },
  { id: 'clients', label: 'Клиенты', icon: '👥' },
  { id: 'finance', label: 'Финансы', icon: '💳' },
  { id: 'video', label: 'Видеосессии', icon: '🎥' },
  { id: 'community', label: 'Сообщество', icon: '🧩' },
  { id: 'security', label: 'Безопасность', icon: '🔒' },
];

function FeatureTabs({ activeTab, onChange }) {
  return (
    <div className="feat-tabs">
      {TABS.map((tab) => (
        <a
          key={tab.id}
          href={`#${tab.id}`}
          className={`feat-tab ${activeTab === tab.id ? 'active' : ''}`}
          onClick={() => onChange(tab.id)}
        >
          <span className="feat-tab-icon" aria-hidden="true">{tab.icon}</span>
          {tab.label}
        </a>
      ))}
    </div>
  );
}

function resolveStartingPriceLabel(plans = []) {
  const paidPrices = plans
    .map((plan) => Number(plan?.monthlyPrice))
    .filter((value) => Number.isFinite(value) && value > 0)
    .sort((left, right) => left - right);

  if (!paidPrices.length) return '';
  return `${new Intl.NumberFormat('ru-RU').format(Math.round(paidPrices[0]))} ₽/месяц`;
}

function Main() {
  const [activeTab, setActiveTab] = useState('calendar');
  const [startingPriceLabel, setStartingPriceLabel] = useState('');

  useEffect(() => {
    let cancelled = false;

    subscriptionsApi.listPlans()
      .then((plans) => {
        if (cancelled) return;
        setStartingPriceLabel(resolveStartingPriceLabel(Array.isArray(plans) ? plans : []));
      })
      .catch(() => {
        if (cancelled) return;
        setStartingPriceLabel('');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const origin = useMemo(() => getSiteOrigin(), []);
  const canonical = useMemo(() => joinUrl(origin, '/'), [origin]);

  const title = 'Функционал платформы — Бюро Души';
  const description = 'Всё, что нужно для практики психолога: календарь, клиенты, финансы, видеосессии, сообщество и безопасность данных.';

  return (
    <div className="b-features-home">
      <Helmet prioritizeSeoTags>
        <title>{title}</title>
        <meta name="description" content={description} />
        {canonical ? <link rel="canonical" href={canonical} /> : null}
        <meta name="robots" content="index, follow" />

        <meta property="og:type" content="website" />
        <meta property="og:locale" content="ru_RU" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        {canonical ? <meta property="og:url" content={canonical} /> : null}

        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>

      <section className="page-hero">
        <div className="hero-inner">
          <div className="page-label">✦ Функционал</div>
          <h1 className="page-title">
            Всё, что нужно
            <br />
            для <span className="m">практики.</span>
            <br />
            <span className="l">В одном месте.</span>
          </h1>
          <p className="page-sub">
            Восемь инструментов, которые заменяют пять разных сервисов.
            Без переключений, без потерь данных, без лишних подписок.
          </p>
          <div className="hero-cta-row">
            <Link className="btn-primary" to="/dashboard">Попробовать бесплатно →</Link>
          </div>
        </div>
      </section>

      <FeatureTabs activeTab={activeTab} onChange={setActiveTab} />

      <section className="feat-block" id="calendar">
        <div className="feat-content">
          <div className="feat-tag"><div className="feat-tag-dot mint" /> Умный Календарь</div>
          <h2 className="feat-block-title">Расписание, которое<br />работает <span className="m">за вас</span></h2>
          <p className="feat-block-desc">Забудьте о бесконечных переписках «когда вам удобно?». Клиенты сами выбирают удобное время из вашего расписания — вы просто получаете уведомление о новой записи.</p>
          <ul className="feat-checklist">
            <li><div className="feat-check fc-mint">✓</div> Встроенный календарь — клиент сам выбирает удобное время</li>
            <li><div className="feat-check fc-mint">✓</div> Автоматические напоминания клиентам за 24 часа и за 1 час до сессии</li>
            <li><div className="feat-check fc-mint">✓</div> Онлайн-бронирование — клиент записывается сам</li>
            <li><div className="feat-check fc-lav">✓</div> Перенос и отмена записи в пару кликов</li>
          </ul>
          <div className="feat-stat-row">
            <div className="feat-stat"><div className="feat-stat-val"><span className="m">80%</span></div><div className="feat-stat-label">меньше пропусков после включения напоминаний</div></div>
            <div className="feat-stat"><div className="feat-stat-val">0</div><div className="feat-stat-label">конфликтов в расписании</div></div>
          </div>
        </div>

        <div className="feat-visual">
          <div className="mock-card">
            <div className="mock-header">
              <div className="wd wr" /><div className="wd wy" /><div className="wd wg" />
              <div className="mock-title">Мой календарь — Апрель 2026</div>
            </div>
            <div className="mock-body">
              <div className="cal-header">
                <div className="cal-month">Апрель 2026</div>
                <div className="cal-nav"><button className="cal-btn" type="button">‹</button><button className="cal-btn" type="button">›</button></div>
              </div>
              <div className="cal-grid">
                <div className="cal-day-label">Пн</div><div className="cal-day-label">Вт</div><div className="cal-day-label">Ср</div>
                <div className="cal-day-label">Чт</div><div className="cal-day-label">Пт</div><div className="cal-day-label">Сб</div><div className="cal-day-label">Вс</div>
                <div className="cal-day other">31</div><div className="cal-day">1</div><div className="cal-day has-event">2</div><div className="cal-day has-event">3</div><div className="cal-day">4</div><div className="cal-day">5</div><div className="cal-day">6</div>
                <div className="cal-day has-event">7</div><div className="cal-day">8</div><div className="cal-day has-event">9</div><div className="cal-day">10</div><div className="cal-day">11</div><div className="cal-day">12</div><div className="cal-day">13</div>
                <div className="cal-day today">14</div><div className="cal-day selected has-event">15</div><div className="cal-day has-event">16</div><div className="cal-day">17</div><div className="cal-day">18</div><div className="cal-day">19</div><div className="cal-day">20</div>
              </div>
              <div className="cal-events">
                <div className="cal-event"><div className="cal-event-time">10:00</div><div className="cal-event-name">Алина Кротова</div><div className="cal-event-tag tag-mint">Подтверждена</div></div>
                <div className="cal-event lav"><div className="cal-event-time">13:30</div><div className="cal-event-name">Дмитрий Морозов</div><div className="cal-event-tag tag-lav">Новая запись</div></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="feat-block alt reverse" id="clients">
        <div className="feat-content">
          <div className="feat-tag"><div className="feat-tag-dot lav" /> База Клиентов</div>
          <h2 className="feat-block-title">Вся история —<br />под <span className="l">рукой</span></h2>
          <p className="feat-block-desc">Карточка каждого клиента хранит всё: историю сессий, ваши заметки, финансы, документы. Вы всегда готовы к сессии.</p>
          <ul className="feat-checklist">
            <li><div className="feat-check fc-lav">✓</div> Личная карточка с полной историей работы</li>
            <li><div className="feat-check fc-lav">✓</div> Хронология сессий с заметками и наблюдениями</li>
            <li><div className="feat-check fc-mint">✓</div> Теги и категории — быстрый поиск</li>
            <li><div className="feat-check fc-mint">✓</div> Приватные заметки видны только вам</li>
          </ul>
          <div className="feat-stat-row">
            <div className="feat-stat"><div className="feat-stat-val">∞</div><div className="feat-stat-label">клиентов на тарифе Премиум</div></div>
            <div className="feat-stat"><div className="feat-stat-val"><span className="l">30 с</span></div><div className="feat-stat-label">среднее время на поиск клиента</div></div>
          </div>
        </div>

        <div className="feat-visual">
          <div className="mock-card">
            <div className="mock-header">
              <div className="wd wr" /><div className="wd wy" /><div className="wd wg" />
              <div className="mock-title">Карточка клиента</div>
            </div>
            <div className="mock-body">
              <div className="client-profile">
                <div className="client-ava">АК</div>
                <div>
                  <div className="client-name">Алина Кротова</div>
                  <div className="client-meta">Клиент с января 2026 · Москва</div>
                </div>
              </div>
              <div className="client-stats">
                <div className="cs-box"><div className="cs-val m">24</div><div className="cs-label">Сессий</div></div>
                <div className="cs-box"><div className="cs-val">6 мес</div><div className="cs-label">В работе</div></div>
                <div className="cs-box"><div className="cs-val">96 000₽</div><div className="cs-label">Оплачено</div></div>
              </div>
              <div className="client-notes-title">Заметки психолога</div>
              <div className="client-note">Фокус на тревоге и перфекционизме. На последней сессии хороший прогресс с техникой заземления.<div className="note-date">14 апреля 2026</div></div>
            </div>
          </div>
        </div>
      </section>

      <section className="feat-block" id="finance">
        <div className="feat-content">
          <div className="feat-tag"><div className="feat-tag-dot mint" /> Финансовый контроль</div>
          <h2 className="feat-block-title"><span className="m">100%</span> дохода —<br />ваш. Всегда.</h2>
          <p className="feat-block-desc">Никаких комиссий с оплат клиентов. Платформа считает доход, напоминает о задолженностях и готовит отчеты.</p>
          <ul className="feat-checklist">
            <li><div className="feat-check fc-mint">✓</div> 0% комиссии с оплат клиентов</li>
            <li><div className="feat-check fc-mint">✓</div> Онлайн-оплата через СБП, карту, ЮKassa</li>
            <li><div className="feat-check fc-lav">✓</div> Финансовая аналитика по месяцам, клиентам и услугам</li>
            <li><div className="feat-check fc-lav">✓</div> Экспорт отчетов для налоговой</li>
          </ul>
          <div className="feat-stat-row">
            <div className="feat-stat"><div className="feat-stat-val"><span className="m">0%</span></div><div className="feat-stat-label">комиссии с клиентов</div></div>
            <div className="feat-stat"><div className="feat-stat-val"><span className="l">+40%</span></div><div className="feat-stat-label">рост дохода в среднем за 3 месяца</div></div>
          </div>
        </div>

        <div className="feat-visual">
          <div className="mock-card">
            <div className="mock-header">
              <div className="wd wr" /><div className="wd wy" /><div className="wd wg" />
              <div className="mock-title">Финансы — Апрель 2026</div>
            </div>
            <div className="mock-body">
              <div className="fin-summary">
                <div className="fin-box green"><div className="fin-label">Доход за месяц</div><div className="fin-val m">76 400₽</div><div className="fin-sub">+12% к марту</div></div>
                <div className="fin-box lav"><div className="fin-label">Ожидается</div><div className="fin-val l">18 000₽</div><div className="fin-sub">+6 сессий</div></div>
              </div>
              <div className="fin-chart">
                <div className="fin-chart-title">Доход по месяцам</div>
                <div className="fin-bars">
                  <div className="fin-bar" style={{ height: '42px' }} />
                  <div className="fin-bar" style={{ height: '52px' }} />
                  <div className="fin-bar" style={{ height: '48px' }} />
                  <div className="fin-bar active" style={{ height: '60px' }} />
                  <div className="fin-bar" style={{ height: '20px', opacity: 0.45 }} />
                </div>
              </div>
              <div className="fin-tx">
                <div className="fin-tx-item"><div className="fin-tx-ico">💳</div><div className="fin-tx-info"><div className="fin-tx-name">Алина Кротова</div><div className="fin-tx-date">14 апреля · Онлайн-сессия</div></div><div className="fin-tx-amt">+4 000₽</div></div>
                <div className="fin-tx-item"><div className="fin-tx-ico">💳</div><div className="fin-tx-info"><div className="fin-tx-name">Дмитрий Морозов</div><div className="fin-tx-date">13 апреля · Онлайн-сессия</div></div><div className="fin-tx-amt">+4 000₽</div></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="feat-block alt reverse" id="video">
        <div className="feat-content">
          <div className="feat-tag"><div className="feat-tag-dot lav" /> Видеосессии</div>
          <h2 className="feat-block-title">Видеосессии прямо<br />внутри <span className="l">платформы</span></h2>
          <p className="feat-block-desc">Не нужен Zoom или другие сервисы. Видео, чат и история сессий в одном месте.</p>
          <ul className="feat-checklist">
            <li><div className="feat-check fc-lav">✓</div> HD-видео без установки приложений</li>
            <li><div className="feat-check fc-lav">✓</div> Автоматическая ссылка на сессию клиенту</li>
            <li><div className="feat-check fc-mint">✓</div> Чат во время видеосессии</li>
            <li><div className="feat-check fc-mint">✓</div> Шифрование соединения</li>
          </ul>
          <div className="feat-stat-row">
            <div className="feat-stat"><div className="feat-stat-val"><span className="l">0</span></div><div className="feat-stat-label">дополнительных сервисов</div></div>
            <div className="feat-stat"><div className="feat-stat-val">HD</div><div className="feat-stat-label">качество видео по умолчанию</div></div>
          </div>
        </div>

        <div className="feat-visual">
          <div className="video-mock">
            <div className="mock-header dark-head">
              <div className="wd wr" /><div className="wd wy" /><div className="wd wg" />
              <div className="mock-title">Бюро Души — Видеосессия</div>
              <div className="video-rec"><div className="rec-dot" /> 42:17</div>
            </div>
            <div className="video-main">
              <div className="video-avatar-big">АК</div>
              <div className="video-name-tag">Алина Кротова</div>
              <div className="video-thumb">🙂</div>
            </div>
            <div className="video-controls">
              <button className="video-btn vb-mic" type="button">🎤</button>
              <button className="video-btn vb-cam" type="button">📷</button>
              <button className="video-btn vb-chat" type="button">💬</button>
              <button className="video-btn vb-end" type="button">✕</button>
              <div className="video-time">14:00 — 15:00</div>
            </div>
          </div>
        </div>
      </section>

      <section className="feat-block" id="community">
        <div className="feat-content">
          <div className="feat-tag"><div className="feat-tag-dot lav" /> Сообщество и знания</div>
          <h2 className="feat-block-title">Больше чем CRM —<br />целая <span className="l">экосистема</span></h2>
          <p className="feat-block-desc">Форум, подкасты, библиотека, курсы и тесты — платформа объединяет психологов и клиентов не только во время сессий.</p>
          <ul className="feat-checklist">
            <li><div className="feat-check fc-lav">✓</div> <strong>Форум</strong> — пространство для вопросов и поддержки</li>
            <li><div className="feat-check fc-lav">✓</div> <strong>Подкасты</strong> — слушайте и публикуйте аудио</li>
            <li><div className="feat-check fc-mint">✓</div> <strong>Литература</strong> — библиотека материалов</li>
            <li className="feat-item-locked">
              <span className="feat-lock">🔒</span>
              <span className="feat-line-text"><strong>Курсы</strong> — образовательные программы <span className="feat-badge-row"><span className="feat-badge-soon">Скоро</span><span className="feat-badge-paid">Доп. тариф</span></span></span>
            </li>
            <li className="feat-item-locked">
              <span className="feat-lock">🔒</span>
              <span className="feat-line-text"><strong>Тесты</strong> — диагностика и динамика <span className="feat-badge-row"><span className="feat-badge-paid">Доп. тариф</span></span></span>
            </li>
          </ul>
          <div className="feat-stat-row">
            <div className="feat-stat"><div className="feat-stat-val"><span className="l">5</span></div><div className="feat-stat-label">форматов контента</div></div>
            <div className="feat-stat"><div className="feat-stat-val"><span className="m">0₽</span></div><div className="feat-stat-label">дополнительных подписок</div></div>
          </div>
        </div>

        <div className="feat-visual">
          <div className="community-list">
            <div className="community-item"><div className="ci-icon">💬</div><div><div className="ci-title">Форум</div><div className="ci-desc">Обсуждения и поддержка</div></div><div className="ci-badge">Новое</div></div>
            <div className="community-item"><div className="ci-icon">🎙</div><div><div className="ci-title">Подкасты</div><div className="ci-desc">Аудио на психологические темы</div></div></div>
            <div className="community-item"><div className="ci-icon mint">📚</div><div><div className="ci-title">Литература</div><div className="ci-desc">Библиотека книг и материалов</div></div></div>
            <div className="community-item disabled"><div className="ci-icon">🎓</div><div><div className="ci-title">Курсы <span className="feat-badge-soon">Скоро</span></div><div className="ci-desc">Образовательные программы</div></div></div>
            <div className="community-item disabled"><div className="ci-icon mint">📝</div><div><div className="ci-title">Тесты <span className="feat-badge-paid">Доп. тариф</span></div><div className="ci-desc">Диагностика и динамика клиентов</div></div></div>
          </div>
        </div>
      </section>

      <section className="feat-block alt reverse" id="security">
        <div className="feat-content">
          <div className="feat-tag"><div className="feat-tag-dot mint" /> Безопасность</div>
          <h2 className="feat-block-title">Данные клиентов под<br /><span className="m">надёжной</span> защитой</h2>
          <p className="feat-block-desc">Платформа помогает специалистам безопасно работать с клиентскими данными и соблюдать требования законодательства.</p>
          <ul className="feat-checklist">
            <li><div className="feat-check fc-mint">✓</div> Инфраструктура соответствует требованиям 152-ФЗ</li>
            <li><div className="feat-check fc-mint">✓</div> Платформа помогает безопасно работать с клиентскими данными</li>
            <li><div className="feat-check fc-lav">✓</div> Серверы и первичный сбор данных — на территории РФ</li>
            <li><div className="feat-check fc-lav">✓</div> AES-256, 2FA и резервное копирование</li>
          </ul>
        </div>

        <div className="feat-visual">
          <div className="sec-mock">
            <div className="sec-mock-header">
              <div className="sec-shield">🛡</div>
              <div>
                <div className="sec-mock-title">Безопасность данных</div>
                <div className="sec-mock-sub">Соответствие 152-ФЗ · Шифрование AES-256</div>
              </div>
            </div>
            <div className="sec-badges">
              <div className="sec-badge-card green"><div className="sec-badge-icon">📄</div><div className="sec-badge-title">152-ФЗ</div><div className="sec-badge-desc">Инфраструктура и процессы соответствуют требованиям законодательства РФ</div></div>
              <div className="sec-badge-card lav"><div className="sec-badge-icon">🔐</div><div className="sec-badge-title">AES-256</div><div className="sec-badge-desc">Шифрование данных на уровне современных SaaS-платформ</div></div>
              <div className="sec-badge-card dark"><div className="sec-badge-icon">🇷🇺</div><div className="sec-badge-title">Серверы РФ</div><div className="sec-badge-desc">Первичный сбор и хранение данных на территории России</div></div>
              <div className="sec-badge-card dark"><div className="sec-badge-icon">🔑</div><div className="sec-badge-title">Контроль доступа</div><div className="sec-badge-desc">2FA, резервное копирование и разграничение доступа</div></div>
            </div>
          </div>
        </div>
      </section>

      <section className="stats-strip" aria-label="Ключевые показатели">
        <div className="stat-item"><div className="stat-val m">11</div><div className="stat-label">инструментов на платформе</div></div>
        <div className="stat-item"><div className="stat-val l">0%</div><div className="stat-label">комиссии с ваших доходов</div></div>
        <div className="stat-item"><div className="stat-val m">7 дней</div><div className="stat-label">бесплатный пробный период</div></div>
        <div className="stat-item"><div className="stat-val w">10 мин</div><div className="stat-label">настройка с нуля</div></div>
      </section>

      <section className="cta-wrap">
        <div className="cta-banner">
          <div className="cta-inner">
            <div className="cta-title">Попробуйте все функции<br />бесплатно — <span className="m">7 дней</span></div>
            <div className="cta-desc">Никакой карты. Никаких скрытых платежей. Просто зарегистрируйтесь и начните работать.</div>
            <Link className="btn-primary cta-main-btn" to="/dashboard">Начать бесплатно →</Link>
            <div className="cta-note">
              Или <Link to="/pricing">посмотрите тарифы</Link>{startingPriceLabel ? ` — от ${startingPriceLabel}` : ''}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Main;
